import React, { useState, useEffect } from "react";
import {
	Button,
	Form,
  InputGroup,
  Modal
} from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import DataTable from "react-data-table-component";
import _ from "lodash";
import styled from "styled-components";

import { setForm } from "../../../redux/slices/StaffFormsSlices";
import { authorizedRequests } from "../../../axios/axios-config";
import SelectComponent from "../../SharedComponents/SelectComponent";
import { toastify } from "../../../helperFunctions/toastify";
import { generateRandomStr } from "../../../helperFunctions/utils";

const customStyles = {
  headCells: {
    style: {
      justifyContent: "center",
    },
  },
  cells: {
    style: {
      justifyContent: "center",
      textAlign: "center",
    },
  },
};

const SwitchWrapper = styled.div`
  label {
    color: black;
    font-weight: bold;
    font-size: 19px;
    line-height: 21px;
  }
`;

const getDefaultValue = () => {
  return {
    drugId: "",
    drugName: "",
    quantity: 0,
    discount: 0,
    unitPrice: 0,
    instruction: "",
    type: "internal",
    isAdd: true,
    tempId: generateRandomStr(10),
  }
}

export default function PatientPrescriptionSheet() {
  const dispatch = useDispatch();

  const { patientPrescriptionSheet, dataId } = useSelector(
		(state) => state.staffForm.sessionForm,
  );

  const [showModal, setShowModal] = useState(false);
  const [products, setProducts] = useState([]);
  const [sheetDrugs, setSheetDrugs] = useState(patientPrescriptionSheet ? JSON.parse(JSON.stringify(patientPrescriptionSheet)) : []);
  const [tempDrug, setTempDrug] = useState(getDefaultValue());
  const [gstTax, setGstTax] = useState(null);

  useEffect(() => {
    authorizedRequests({
      url: "product/listOfProducts",
      method: "get",
    }).then((res) => {
      setProducts(res?.result?.data ?? []);
    });

    authorizedRequests({
      url: "taxSetting/listOfTaxSetting",
      method: "get",
    }).then((res) => {
      const resultGstTax = res?.result?.data?.find(x => x.name?.toLowerCase() === "gst");
      setGstTax(resultGstTax ? resultGstTax : null);
    })
  }, []);


  const handleChange = (field, value) => {
    setTempDrug({
      ...tempDrug,
      [field]: value
    })
  }

  const handleChangeDrugId = (valueDrugId, valueDrugName, valuePrice) => {
    setTempDrug({
      ...tempDrug,
      drugId: valueDrugId,
      drugName: valueDrugName,
      unitPrice: valuePrice
    })
  }

  const getSubtotal = (drugId, quantity) => {
    const selectedDrug = products?.find(x => x._id === drugId);
    if (!selectedDrug) {
      return 0;
    }
    return selectedDrug.unitSellingPrice * quantity;
  }

  const getTotal = (drugId, quantity, discount) => {
    const subtotal = getSubtotal(drugId, quantity);
    return subtotal - discount;
  }

  const handleSubmit = async() => {
    let totalGST = 0;
    const dataDrugs = sheetDrugs.map(x => {
      if (x.type === "external") {
        delete x.drugId;
      } 

      if (x.type === "internal") {
        totalGST += (x.quantity * x.unitPrice) - x.discount;
      }

      return x;
    });

    if (gstTax !== null && totalGST !== 0) {
      if (gstTax.typeValue === "Percentage") {
        totalGST += (totalGST * gstTax.value / 100);
      } else {
        totalGST += gstTax.value;
      }
    }

    const { result, error } = await authorizedRequests({
			url: "session/updateSession",
			method: "post",
			data: {
				dataId: dataId,
        patientPrescriptionSheet: dataDrugs,
        prescriptionGSTAmount: totalGST
			},
		});
		if (error) {
			toastify(error?.message, "error");
			return;
		} else {
      dispatch(
        setForm({
          parent: "sessionForm",
          child: "patientPrescriptionSheet",
          value: dataDrugs,
        }),
      );
      toastify("Prescription Sheet saved succesfully", "success");
			return;
    }
  }

  const columns = [
		{
			name: "Drug",
			selector: (row) => row.drugId?.name ? row.durgId?.name : row.drugName,
			wrap: true,
		},
		{
			name: "Instruction",
			selector: (row) => row.instruction,
			wrap: true,
		},
		{
			name: "Quantity",
			selector: (row) => row.quantity,
			wrap: true,
		},
		{
			name: "Subtotal",
			selector: (row) => getSubtotal(row.drugId, row.quantity),
			wrap: true,
    },
    {
			name: "Discount Amount",
			selector: (row) => row.discount,
			wrap: true,
		},
		{
			name: "Total",
			selector: (row) => getTotal(row.drugId, row.quantity, row.discount),
			wrap: true,
    },
    {
			name: "Action",
			selector: (row) => {
        return (
        <div className="d-flex ">
            <div
              className="cursor-pointer"
              onClick={() => {
								setTempDrug(row);
								setShowModal(true);
							}}
            >
              <i className="fa fa-pen"></i>
            </div>

						<div
							className="ms-3  cursor-pointer icon-width"
							onClick={() => setSheetDrugs(sheetDrugs.filter(x => x.drugName !== row.drugName))}
						>
							<i className="fa fa-trash"></i>
						</div>
					</div>
        );
      },
			wrap: true,
		},
  ];

	return (
		<div className="consent-container px-4 pb-4">
      <div className="addEdit">
        <div className="pb-4">
          <Button
            className="form-button"
            style={{ width: "200px" }}
            onClick={() => setShowModal(true)}
          >
            +Add Drug
          </Button>
        </div>
        <div className="highlighted-container p-4 mt-2 mb-4">
          <p className="font-600-24 grey mt-3">Prescription Sheet</p>
          <div>
            <p className="font-600-24 grey mt-3" style={{fontSize: "18px"}}>Internal</p>
            <DataTable
              columns={columns}
              data={sheetDrugs.filter(x => x.type === "internal").map((x, index) => (
                {
                  ...x,
                  index: index,
                  key: index
                }
              ))}
              customStyles={customStyles}
            />
          </div>
          <div>
            <p className="font-600-24 grey mt-3" style={{fontSize: "18px"}}>External</p>
            <DataTable
              columns={columns}
              data={sheetDrugs.filter(x => x.type === "external").map((x, index) => (
                {
                  ...x,
                  index: index,
                  key: index
                }
              ))}
              customStyles={customStyles}
            />
          </div>
        </div>

        <div className="mt-3 d-flex align-self-center col-12">
          <Button
            style={{ width: "150px" }}
            className="addItem ms-auto"
            onClick={() => handleSubmit()}
          >
            Save
          </Button>
        </div>
      </div>

      <Modal
        centered
        size={"lg"}
        show={showModal}
        onHide={() => {
          setShowModal(false);
          setTempDrug(getDefaultValue());
        }}
      >
        <Modal.Header>
          <div className="d-flex justify-content-between w-100">
            <div>
              <span className="grey font-600-24 ">
                Drug
              </span>
            </div>
            <i
              className="fa fa-xmark align-self-center pointer"
              onClick={() => {
                setShowModal(false);
                setTempDrug(getDefaultValue());
              }}
              style={{fontSize: "30px"}}
            ></i>
          </div>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex w-100">
            <div className="col-6" style={{paddingRight: "10px"}}>
              <Form.Group>
                <SwitchWrapper>
                  <Form.Check 
                    type="switch"
                    id="custom-switch"
                    label="Is External"
                    checked={tempDrug.type === "internal" ? false : true}
                    onChange={e => {
                      handleChange("type", e.target.checked === true ? "external" : "internal");
                    }}
                  />
                </SwitchWrapper>
              </Form.Group>
              {
                tempDrug.type === "internal" ? (
                  <Form.Group>
                    <Form.Label className="black-label-text">
                      Drug
                    </Form.Label>
                    <SelectComponent
                      searchEnabled
                      singleSelect
                      selectedValue={tempDrug.drugId}
                      options={products?.map((product) => (
                        {
                          label: product.name,
                          value: product._id
                        }
                      ))}
                      placeholder="Write..."
                      onChange={(e) => {
                        const selectedDrug = products.find(x => x._id === e.value);
                        handleChangeDrugId(e.value, selectedDrug?.name, selectedDrug?.unitSellingPrice);
                      }}
                      name={"drugId"}
                    />
                  </Form.Group>
                ) : (
                  <Form.Group>
                    <Form.Label className="black-label-text">
                      Drug
                    </Form.Label>
                    <InputGroup className="mb-3 ">
                      <Form.Control
                        type="text"
                        className="hideArrows"
                        name={"drugName"}
                        placeholder="Write..."
                        onChange={(e) => handleChange("drugName", e.target.value)}
                        value={tempDrug.drugName}
                      />
                    </InputGroup>
                  </Form.Group>
                )
              }
              <Form.Group>
                <Form.Label className="black-label-text">
                  Quantity
                </Form.Label>
                <InputGroup className="mb-3 ">
                  <Form.Control
                    type="number"
                    className="hideArrows"
                    name={"quantity"}
                    placeholder="Write..."
                    onChange={(e) => handleChange("quantity", e.target.value)}
                    value={tempDrug.quantity}
                  />
                </InputGroup>
              </Form.Group>
              <Form.Group>
                <Form.Label className="black-label-text">
                  Subtotal
                </Form.Label>
                <InputGroup className="mb-3 disabled-input">
                  <Form.Control
                    type="number"
                    className="hideArrows"
                    name={"subtotal"}
                    disabled={true}
                    value={getSubtotal(tempDrug.drugId, tempDrug.quantity)}
                  />
                </InputGroup>
              </Form.Group>
              <Form.Group>
                <Form.Label className="black-label-text">
                  Discount
                </Form.Label>
                <InputGroup className={`mb-3 ${tempDrug.type === "external" ? "disabled-input" : ""}`}>
                  <Form.Control
                    type="number"
                    className="hideArrows"
                    name={"discount"}
                    placeholder="Write..."
                    disabled={tempDrug.type === "external" ? true : false}
                    onChange={(e) => handleChange("discount", e.target.value)}
                    value={tempDrug.discount}
                  />
                </InputGroup>
              </Form.Group>
              <Form.Group>
                <Form.Label className="black-label-text">
                  Total
                </Form.Label>
                <InputGroup className="mb-3 disabled-input">
                  <Form.Control
                    type="number"
                    className="hideArrows"
                    name={"total"}
                    disabled={true}
                    value={getTotal(tempDrug.drugId, tempDrug.quantity, tempDrug.discount)}
                  />
                </InputGroup>
              </Form.Group>
            </div>
            <div className="col-6" style={{paddingLeft: "10px"}}>
              <Form.Group>
                <Form.Label className="black-label-text">
                  Instruction
                </Form.Label>
                <InputGroup className="mb-3 ">
                  <Form.Control
                    as="textarea"
                    className="hideArrows"
                    name={"instruction"}
                    rows={5}
                    onChange={e => handleChange("instruction", e.target.value)}
                    value={tempDrug.instruction}
                  />
                </InputGroup>
              </Form.Group>
            </div>
          </div>
          <Button
            className="addItem ms-auto mt-3"
            style={{ borderRadius: "25px", width: "150px" }}
            onClick={() => {
              if (tempDrug.isAdd !== true) {
                setSheetDrugs((prevState) => {
                  const updatedDrugs = [...prevState];
                  let selectedDrug = null;
                  if (tempDrug._id) {
                    selectedDrug = updatedDrugs.find(x => x._id === tempDrug._id);
                  } else {
                    selectedDrug = updatedDrugs.find(x => x.tempId === tempDrug.tempId);
                  }

                  if (selectedDrug) {
                    if (tempDrug.drugId) {
                      selectedDrug.drugId = tempDrug.drugId;
                    }
                    if (tempDrug.type === "internal") {
                      const selectedDrug = products.find(x => x._id === tempDrug.drugId);
                      selectedDrug.drugName = selectedDrug?.name;
                    } else {
                      selectedDrug.drugName = tempDrug.drugName;
                    }
                    selectedDrug.quantity = tempDrug.quantity;
                    selectedDrug.discount = tempDrug.discount;
                    selectedDrug.instruction = tempDrug.instruction;
                    selectedDrug.type = tempDrug.type;
                    selectedDrug.unitPrice = tempDrug.unitPrice;
                  }

                  return updatedDrugs;
                })
              } else {
                setSheetDrugs([...sheetDrugs, {...tempDrug, isAdd: false}]);
              }
              setTempDrug(getDefaultValue());
              setShowModal(false);
            }}
          >
            Save
          </Button>
        </Modal.Body>
      </Modal>
    </div>
	);
}
